import { useMemo } from "react";

import { CurrencyWithDiscount } from "@chef/components";
import { useAppDispatch, useAppSelector } from "@chef/state-management/hooks";
import {
  openModal,
  selectDeviationBasket,
  useProductCategoriesQuery,
  useProductsByCategoriesQuery,
} from "@chef/state-management";
import {
  getDiscountedAmount,
  getImageFromVariation,
  getPriceOfProductByVariation,
  getStandaloneProductCategoryIdsFromProductCategories,
  getVariationFromProductsByCategories,
} from "@chef/state-management/helpers";
import { isAddonProduct, isStandaloneProduct } from "@chef/helpers";
import { language } from "@chef/constants";
import { isEmptyArray } from "@chef/utils/array";
import { nonNullable } from "@chef/utils/nonNullable";
import { Controls } from "@chef/smart-components";
import { ProductCard } from "./ProductCard";

type ProductModalArgs = {
  productId: string;
  variationId: string;
};

interface StandaloneProductListProps {
  week: number;
  year: number;
}

export const StandaloneProductList = ({
  week,
  year,
}: StandaloneProductListProps) => {
  const dispatch = useAppDispatch();
  const basket = useAppSelector(selectDeviationBasket({ week, year }));

  const { data: productCategoriesData } = useProductCategoriesQuery();

  const { data: productsByCategoriesData } = useProductsByCategoriesQuery(
    {
      categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
        productCategoriesData?.productCategories || [],
      ),
      week,
      year,
    },
    { skip: !productCategoriesData?.productCategories },
  );

  const displayableProducts = useMemo(() => {
    if (!basket?.products) {
      return [];
    }

    const standaloneOrAddonProducts = basket.products.filter(
      (p) => isStandaloneProduct(p) || isAddonProduct(p),
    );

    const productsWithVariations = standaloneOrAddonProducts.map((bp) => {
      const variation = getVariationFromProductsByCategories({
        productsByCategories:
          productsByCategoriesData?.productsByCategories || [],
        variationId: bp.variationId,
        productId: bp.productId,
      });

      if (!variation) {
        return null;
      }

      return {
        ...bp,
        variation,
      };
    });

    return productsWithVariations.filter(nonNullable);
  }, [basket?.products, productsByCategoriesData]);

  if (
    !displayableProducts ||
    !productsByCategoriesData ||
    isEmptyArray(displayableProducts)
  ) {
    return null;
  }

  const openStandaloneProductModal = ({
    productId,
    variationId,
  }: ProductModalArgs) => {
    dispatch(
      openModal({
        productId,
        variationId,
        week,
        year,
        view: "deviation",
        basketType: "deviation",
        id: "STANDALONE_PRODUCT",
      }),
    );
  };

  return (
    <div className="grid grid-cols-1 gap-2">
      {displayableProducts.map(({ variation, variationId, productId }) => {
        const discount = getDiscountedAmount(variation);
        const totalPrice = getPriceOfProductByVariation(variation);
        const imageUrl = getImageFromVariation({
          variation,
        });

        return (
          <ProductCard
            key={variationId}
            imageUrl={imageUrl}
            title={variation.name}
            onClick={() => {
              openStandaloneProductModal({
                productId,
                variationId,
              });
            }}
          >
            <CurrencyWithDiscount
              language={language}
              discount={discount}
              total={totalPrice}
              emphasized
            />
            <Controls
              basketType="deviation"
              week={week}
              year={year}
              variation={variation}
            />
          </ProductCard>
        );
      })}
    </div>
  );
};
